// InvoiceStatementsTable.jsx — client-mode DataTable for invoice statements.
// Receives the page's already-filtered rows. Clicking a row expands an inline detail
// strip; the row ▾ menu's "View statement" opens the statement document directly
// (no summary — the table already shows the per-statement detail).
import React, { useMemo } from "react";
import DataTable from "./datatable/DataTable";
import { invoiceStatementsTableConfig } from "../tableConfigs/invoiceStatements";
import { makeClientAdapter } from "./datatable/clientAdapter";
import InvoiceStatementRowMenu from "./InvoiceStatementRowMenu";
import ModalDialogue from "./ModalDialogue";
import PdfModal from "./PdfModal";
import StatementDetailStrip from "./StatementDetailStrip";
import TermsCell from "./TermsCell";
import useStatementActions from "../hooks/useStatementActions";

// Renders the ▾ menu for a single row. A child component so the actions hook is
// scoped per row (it owns that row's delete-confirm dialog + statement-viewer state).
function RowActions({ row, activeSection, onDeleted }) {
   const { goReconcile, goDownload, viewStatement, statementView, closeStatementView, requestDelete, dialogState, onDialogResponse } = useStatementActions(
      {
         invStatId: row.inv_stat_id,
         dpaId: row.doc_part_id,
         docId: row.doc_id,
         supplierName: row.sm_name,
         statementDate: row.sup_state_frmt_statement_date,
         grade: row?.dpa_confidence_breakdown?.grade,
         activeSection,
         displayName: row.sm_name,
      },
      { onDeleted }
   );

   const noStatement = row.inv_stat_id == null || row.inv_stat_id === "";
   const items = [
      { label: "Reconcile", onSelect: goReconcile, disabled: noStatement },
      { label: "Download", onSelect: goDownload, disabled: !row.doc_id },
      { label: "Delete", onSelect: requestDelete, disabled: !row.doc_part_id },
   ];

   return (
      <>
         <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
            {noStatement ? (
               <span className="text-secondary" style={{ cursor: "default" }}>View</span>
            ) : (
               <a href="#" onClick={(e) => { e.preventDefault(); viewStatement(); }}>View</a>
            )}
            <div style={{ borderLeft: "1px solid #ccc", height: "16px" }} />
            <InvoiceStatementRowMenu rowKey={row.doc_part_id} items={items} />
         </div>
         <ModalDialogue
            isModalDialogueOpen={dialogState.isModalDialogueOpen}
            question={dialogState.question}
            onModalDialogueResponse={onDialogResponse}
         />
         {statementView.open && (
            <PdfModal
               imageData={statementView.imageData}
               propScale={1.0}
               docObj={{ name: row.doc_orig_name, uploadOn: row.doc_upload_on, uploadBy: row.doc_upload_by }}
               onCloseModal={closeStatementView}
            />
         )}
      </>
   );
}

export default function InvoiceStatementsTable({ rows, activeSection, onDeleted, onChanged, loading }) {
   const adapter = useMemo(() => makeClientAdapter(), []);

   // Inject per-row renderers (actions menu + terms picker) without mutating the shared config.
   const config = useMemo(() => ({
      ...invoiceStatementsTableConfig,
      columns: invoiceStatementsTableConfig.columns.map((c) => {
         if (c.field === "__actions") {
            return { ...c, renderCell: (_v, row) => (
               <RowActions row={row} activeSection={activeSection} onDeleted={onDeleted} />
            ) };
         }
         if (c.field === "terms_label") {
            return { ...c, renderCell: (_v, row) => <TermsCell row={row} onSaved={onChanged} /> };
         }
         return c;
      }),
   }), [activeSection, onDeleted, onChanged]);

   return (
      <div className="data-table-card">
         <DataTable
            config={config}
            adapter={adapter}
            clientRows={rows}
            isLoading={loading}
            renderExpanded={(row) => <StatementDetailStrip row={row} />}
         />
      </div>
   );
}
